import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useProductFormAPI } from './hooks/useProductFormAPI';
import { initializeForm, resetForm } from '../../../../../store/actions/productFormActions';
import { RootState } from '../../../../../store/types';
import { ProductForm } from './ProductForm';
import { Box, Button, Drawer, IconButton, Typography } from '@material-ui/core';
import { useProductFormStyles } from './ProductForm.style';
import { FormattedMessage } from 'react-intl';
import CloseIcon from '@material-ui/icons/Close';
export interface ProductCreatorLogic {
  handleSubmit: (event: React.FormEvent) => Promise<void>;
  isLoading: boolean;
  hasError: boolean;
  error: any;
  title: string;
  submitButtonText: string;
}

export interface ProductCreatorProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (product: any, shouldNavigate?: boolean) => void;
}

export const useProductCreator = ({ open, onClose, onSubmit }: ProductCreatorProps): ProductCreatorLogic => {
  const dispatch = useDispatch();
  
  // Get form state from Redux
  const formData = useSelector((state: RootState) => state.productForm);
  const { isFormValid } = formData;
  
  // API hooks
  const { 
    createProductState, 
    createNewProduct,
    isLoading,
    hasError,
    error,
    resetError
  } = useProductFormAPI();

  // Initialize form when component mounts
  useEffect(() => {
    if (open) {
      dispatch(initializeForm(null, false));
    }
  }, [open, dispatch]);
  
  // Reset form and error when closed
  useEffect(() => {
    if (!open) {
      dispatch(resetForm());
      resetError();
    }
  }, [open, dispatch, resetError]);

  // Handle API success states
  useEffect(() => {
    if (createProductState.value && !createProductState.loading && createProductState.loaded) {
      const id = createProductState.value?.data?.id || formData.name.value.trim();
      
      if (onSubmit) {
        const productData = {
          name: formData.name.value.trim(),
          description: formData.description.value.trim(),
          visibility: formData.visibility.value,
          approvalFlow: formData.approvalFlow.value,
          id
        };
        onSubmit(productData, true);
      }
      
      onClose();
    }
  }, [createProductState, formData, onSubmit, onClose]);
  
  // Form submission handler
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isFormValid) return;
    
    try {
      await createNewProduct();
    } catch (error) {
      console.error('Product creation failed:', error);
    }
  }, [isFormValid, createNewProduct]);

  return {
    handleSubmit,
    isLoading,
    hasError,
    error,
    title: 'Add new Product',
    submitButtonText: 'Create'
  };

}

export const ProductCreator: React.FC<ProductCreatorProps> = ({ open, onClose, onSubmit }) => {
  const classes = useProductFormStyles();
  
  // Get form state from Redux
  const formData = useSelector((state: RootState) => state.productForm);
  const { isFormValid } = formData;
  
  // Use creator logic
  const { handleSubmit, isLoading, hasError, error, title, submitButtonText } = useProductCreator({ open, onClose, onSubmit });
  
  return (
    <Drawer
      anchor="right"
      open={open}
      variant="temporary"
      onClose={onClose}
      classes={{
        paper: classes.drawer,
      }}
      ModalProps={{
        classes: {
          root: classes.modalRoot,
          backdrop: classes.backdrop
        },
        hideBackdrop: false,
      }}
      SlideProps={{
        mountOnEnter: true,
        unmountOnExit: true,
      }}
    >
      <Box className={classes.formContainer}>
        {/* Header with title and close button */}
        <Box className={classes.header}>
          <Typography className={classes.title}>
            <FormattedMessage defaultMessage="Add new product" />
          </Typography>
          <IconButton onClick={onClose} className={classes.closeButton} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        <ProductForm
          handleSubmit={handleSubmit}
          hasError={hasError}
          error={error}
          onClose={onClose}
          isLoading={isLoading}
          submitButtonText={submitButtonText}
          isEditMode={false}
        />
         {/* Buttons */}
         <Box className={classes.buttonsContainer}>
          <Button
            onClick={onClose}
            className={classes.cancelButton}
            disabled={isLoading}
          >
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            className={classes.createButton}
            disabled={!isFormValid || isLoading}
          >
            <FormattedMessage defaultMessage={"Create"} />
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};